import React, { useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import EmptyState from '../../components/ui/EmptyState';
import api from '../../api';

const gerarSenha = () => Math.random().toString(36).slice(2, 6) + Math.floor(1000 + Math.random() * 9000);

const PAPEIS = {
  arbitro: { endpoint: 'arbitros', rotulo: 'árbitro', titulo: 'Novo árbitro', loginUrl: '/arbitro/login', acesso: 'só vê a tela das partidas (depois do check-in na pelada)', vazio: 'Nenhum árbitro cadastrado' },
  goleiro: { endpoint: 'goleiros', rotulo: 'goleiro', titulo: 'Novo goleiro', loginUrl: '/goleiro/login', acesso: 'só vê a tela de check-in da pelada em que está escalado', vazio: 'Nenhum goleiro cadastrado' },
};
const moeda = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

// Logins de árbitros e goleiros (fora da tabela de usuários), com valor por pelada e vínculo fixo.
const ArbitrosAdmin = ({ onMessage, papel = 'arbitro' }) => {
  const cfg = PAPEIS[papel];
  const [arbitros, setArbitros] = useState([]);
  const [novo, setNovo] = useState(null);
  const [senhaDialog, setSenhaDialog] = useState(null);
  const [criado, setCriado] = useState(null);
  const [excluir, setExcluir] = useState(null);

  const carregar = async () => {
    try {
      const response = await api.get(cfg.endpoint);
      setArbitros(response.data.lista || response.data.arbitros);
    } catch (error) {
      onMessage && onMessage(`Erro ao carregar ${cfg.rotulo}s`, 'error');
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const criar = async () => {
    try {
      await api.post(cfg.endpoint, novo);
      setCriado({ login: novo.login.trim().toLowerCase(), password: novo.password });
      setNovo(null);
      carregar();
    } catch (error) {
      onMessage && onMessage(error.response?.data?.error || `Erro ao criar ${cfg.rotulo}`, 'error');
    }
  };

  const atualizar = async (id, dados, mensagem) => {
    try {
      await api.put(`${cfg.endpoint}/${id}`, dados);
      onMessage && onMessage(mensagem);
      carregar();
    } catch (error) {
      onMessage && onMessage(error.response?.data?.error || 'Erro ao atualizar', 'error');
    }
  };

  const copiarAcesso = (dados) => {
    const texto = `Acesso de ${cfg.rotulo} - Riviera FC\n${window.location.origin}${cfg.loginUrl}\nLogin: ${dados.login}\nSenha: ${dados.password}`;
    navigator.clipboard.writeText(texto).then(() => onMessage && onMessage('Acesso copiado.'));
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
        <Typography variant="body2" color="text.secondary">
          O {cfg.rotulo} entra em <strong>{cfg.loginUrl}</strong> e {cfg.acesso}. Com <strong>vínculo fixo</strong>, entra automaticamente na escala de toda pelada nova.
        </Typography>
        <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setNovo({ nome: '', login: '', password: gerarSenha(), valorPorPelada: '', chavePix: '', fixo: true })} sx={{ flexShrink: 0 }}>
          {cfg.titulo}
        </Button>
      </Stack>

      {arbitros.length === 0 ? (
        <EmptyState title={cfg.vazio} />
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Login</TableCell>
                <TableCell>Valor/pelada</TableCell>
                <TableCell>Chave PIX</TableCell>
                <TableCell>Vínculo fixo</TableCell>
                <TableCell>Último acesso</TableCell>
                <TableCell>Ativo</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {arbitros.map((a) => (
                <TableRow key={a._id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{a.nome}</TableCell>
                  <TableCell><Chip size="small" label={a.login} variant="outlined" /></TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      defaultValue={a.valorPorPelada || 0}
                      onBlur={(e) => Number(e.target.value) !== Number(a.valorPorPelada || 0) && atualizar(a._id, { valorPorPelada: e.target.value }, `Valor atualizado para ${moeda(e.target.value)}`)}
                      sx={{ width: 110 }}
                      InputProps={{ startAdornment: <span style={{ marginRight: 4, color: '#5B6472' }}>R$</span> }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      placeholder="CPF, e-mail, celular..."
                      defaultValue={a.chavePix || ''}
                      onBlur={(e) => e.target.value.trim() !== (a.chavePix || '') && atualizar(a._id, { chavePix: e.target.value.trim() }, 'Chave PIX atualizada')}
                      sx={{ width: 200 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Switch checked={Boolean(a.fixo)} onChange={(e) => atualizar(a._id, { fixo: e.target.checked }, e.target.checked ? 'Vínculo fixo ativado' : 'Vínculo fixo removido')} />
                  </TableCell>
                  <TableCell>{a.lastLogin ? new Date(a.lastLogin).toLocaleString('pt-BR') : '—'}</TableCell>
                  <TableCell>
                    <Switch checked={a.ativo} onChange={(e) => atualizar(a._id, { ativo: e.target.checked }, e.target.checked ? 'Árbitro ativado' : 'Árbitro desativado')} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Redefinir senha"><IconButton size="small" onClick={() => setSenhaDialog({ ...a, password: gerarSenha() })}><KeyRoundedIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Excluir"><IconButton size="small" color="error" onClick={() => setExcluir(a)}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(novo)} onClose={() => setNovo(null)} fullWidth maxWidth="xs">
        <DialogTitle>{cfg.titulo}</DialogTitle>
        {novo && (
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField label="Nome" value={novo.nome} onChange={(e) => setNovo({ ...novo, nome: e.target.value })} fullWidth autoFocus />
              <TextField label="Login" value={novo.login} onChange={(e) => setNovo({ ...novo, login: e.target.value.replace(/\s/g, '').toLowerCase() })} helperText="Letras, números, ponto ou hífen" fullWidth />
              <TextField label="Senha" value={novo.password} onChange={(e) => setNovo({ ...novo, password: e.target.value })} helperText="Mínimo 6 caracteres" fullWidth />
              <TextField label="Valor por pelada" type="number" value={novo.valorPorPelada} onChange={(e) => setNovo({ ...novo, valorPorPelada: e.target.value })} InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>R$</span> }} fullWidth />
              <TextField label="Chave PIX" value={novo.chavePix || ''} onChange={(e) => setNovo({ ...novo, chavePix: e.target.value })} helperText="Usada pela diretoria para fazer o pagamento" fullWidth />
              <FormControlLabel control={<Switch checked={Boolean(novo.fixo)} onChange={(e) => setNovo({ ...novo, fixo: e.target.checked })} />} label="Vínculo fixo (entra em toda pelada nova)" />
            </Stack>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNovo(null)} color="inherit">Cancelar</Button>
          <Button onClick={criar} variant="contained">Criar login</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(criado)} onClose={() => setCriado(null)} fullWidth maxWidth="xs">
        <DialogTitle>Acesso criado</DialogTitle>
        {criado && (
          <DialogContent>
            <Alert severity="success" sx={{ mb: 2 }}>Envie estes dados ao árbitro. A senha não é exibida novamente.</Alert>
            <Typography>Login: <strong>{criado.login}</strong></Typography>
            <Typography>Senha: <strong>{criado.password}</strong></Typography>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button startIcon={<ContentCopyRoundedIcon />} onClick={() => copiarAcesso(criado)}>Copiar acesso</Button>
          <Button onClick={() => setCriado(null)} variant="contained">Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(senhaDialog)} onClose={() => setSenhaDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>Redefinir senha</DialogTitle>
        {senhaDialog && (
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>Nova senha para <strong>{senhaDialog.nome}</strong> ({senhaDialog.login}).</DialogContentText>
            <TextField label="Nova senha" value={senhaDialog.password} onChange={(e) => setSenhaDialog({ ...senhaDialog, password: e.target.value })} fullWidth />
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSenhaDialog(null)} color="inherit">Cancelar</Button>
          <Button
            variant="contained"
            onClick={async () => {
              await atualizar(senhaDialog._id, { password: senhaDialog.password }, 'Senha redefinida');
              setCriado({ login: senhaDialog.login, password: senhaDialog.password });
              setSenhaDialog(null);
            }}
          >
            Salvar senha
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(excluir)} onClose={() => setExcluir(null)}>
        <DialogTitle>Excluir {cfg.rotulo}</DialogTitle>
        <DialogContent>
          <DialogContentText>Excluir o login de {excluir?.nome}?</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExcluir(null)} color="inherit">Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              await api.remove(`${cfg.endpoint}/${excluir._id}`);
              setExcluir(null);
              carregar();
              onMessage && onMessage('Cadastro excluído');
            }}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ArbitrosAdmin;
